import type { Translations } from './types';

const en: Translations = {
    next: 'Next',
    storyReadPrompt: 'Please read the story.',
    backToStory: '← Back to Story',
    nextArrow: 'Next →',
    finishAssessment: 'Finish Assessment →',
    common_step: 'Step',

    questionsStory1: 'Questions — Story 1',
    questionsStory2: 'Questions — Story 2',
    lastSetOfQuestions: 'Last set of questions — almost done! 🎉',
    typeAnswerHere: 'Type your answer here...',
    backCountSingular: '📖 You returned to this page {n} time to re-read the story.',
    backCountPlural: '📖 You returned to this page {n} times to re-read the story.',

    assessmentComplete: 'Assessment Complete! 🎉',
    assessmentSubtitle: "You've taken an important step towards understanding learning differences",
    returnToHome: 'Return to Home',
    assessmentSuccessfullyCompleted: 'Assessment Successfully Completed',
    amazingJob: 'Amazing Job!',
    youCrushedIt: "You crushed it! Here's what you accomplished:",
    whatsNext: "What's Next?",
    getReadyForResults: 'Get ready for your personalized results',
    youDidIt: 'You Did It!',
    youDidItDesc:
      'Your commitment to understanding learning differences is truly inspiring. While others might have given up, you saw it through to the end!',

    heroLine1: 'DYSLEXIA',
    heroLine2: 'SCREENING',
    heroTagline: 'in one sitting',
    heroSubtitle:
      'A browser-based, multi-modal assessment that gives you a written report as soon as you finish — no clinic visit, no waiting list, no special hardware.',
    heroBetaLabel: 'BETA',
    startAssessment: 'Start',
    trustedBy: 'Trusted by educators and researchers worldwide',

    pfp_privacyBadge: 'Privacy & Compliance',
    pfp_privacyHeading: 'We take data protection seriously with full GDPR and MDR compliance',
    pfp_formBadge: 'Start Your Assessment',
    pfp_ctaHeading: 'DYSLEXIA SCREENING',
    pfp_ctaDesc: 'Join thousands who have discovered unique insights about their reading style through eye-gaze tracking and keyboard dynamics.',
    pfp_ctaFooter: 'No time limit • At your own pace • 100% confidential',
    pfp_whyTitle: 'Why DysTest?',
    pfp_whySubtitle: 'Discover what makes our eye-tracking assessment the most accurate and reliable solution',
    pfp_whyDesc: 'DysTest combines over 20 years of professional expertise in dyslexia research with a lifetime of lived experience. By reverse-engineering how dyslexic and neurotypical readers process text, our AI learns to distinguish subtle reading patterns that traditional tests miss.',
    pfp_feature1Title: 'Eye-Gaze, Keystroke & Multilingual Insights',
    pfp_feature1Desc: 'Advanced AI analyzes eye movements, facial cues, and keystrokes to reveal learning patterns unique to every reader — in any language. DysTest adapts intelligently to multilingual users for precise, personalized understanding.',
    pfp_feature2Title: 'Instant, Actionable Results',
    pfp_feature2Desc: 'Get clear, science-backed insights within minutes. Our reports translate complex data into easy-to-understand recommendations that empower parents, educators, and learners alike.',
    pfp_feature3Title: 'Privacy & Compliance First',
    pfp_feature3Desc: 'Your data is encrypted, anonymized, and handled under the highest ethical and regulatory standards — fully compliant with GDPR and MDR. Nothing is shared or used beyond improving your DysTest experience.',
    pfp_feature4Title: 'Accessible, Human-Centered Design',
    pfp_feature4Desc: 'DysTest lets you screen at your own pace — calm, intuitive, and pressure-free. Designed for inclusivity, accessibility, and meaningful understanding, it redefines how learning differences are discovered and supported.',
    pfp_finalCtaDesc: 'Start your dyslexia screening today — designed to understand how you read, not just what you read. 15 minutes, no download, no specialist needed.',

    hiw_badge: 'Simple 8-Step Process',
    hiw_heading: 'How the Assessment Works',
    hiw_subtext: 'From registration to a clinically-informed dyslexia report — entirely in-browser, no downloads required.',
    hiw_step1Title: 'Register',
    hiw_step1Desc: 'Enter name, age and preferred language. No account required — takes under 2 minutes.',
    hiw_step2Title: 'Eye Calibrate',
    hiw_step2Desc: 'Follow 16 animated dots. The AI iris tracker maps your unique gaze signature to the screen.',
    hiw_step3Title: 'Words & Icons',
    hiw_step3Desc: 'Match words to pictures while we measure recognition speed and visual-phonological processing.',
    hiw_step4Title: 'RAN Speed Test',
    hiw_step4Desc: 'Name a sequence of colours or objects aloud as fast as you can. Measures retrieval fluency.',
    hiw_step5Title: 'Stories & Questions',
    hiw_step5Desc: 'Read two age-appropriate stories and answer comprehension questions. Eye movements and blinks are recorded.',
    hiw_step6Title: 'Word Writing',
    hiw_step6Desc: 'Type words from memory. Keystroke timing and error patterns reveal spelling and motor processing.',
    hiw_step7Title: 'Feedback',
    hiw_step7Desc: 'Share your experience and any concerns. This context helps calibrate the clinical interpretation.',
    hiw_step8Title: 'Get Results',
    hiw_step8Desc: 'Receive a detailed report with dyslexia risk indicators, gaze heatmaps and reading metrics.',

    pf_whoTaking: 'Who is taking the test?',
    pf_myself: 'Myself',
    pf_myselfAge: 'Age 13+',
    pf_child: 'Child',
    pf_childAgeRange: 'Age 5-12',
    pf_verifyEmailTitle: 'Verify Your Email',
    pf_parentalConsentStepTitle: 'Parental Consent',
    pf_verifyEmailDesc: 'Enter the code sent to your email to complete registration',
    pf_consentDesc: 'Please review and provide consent for data processing',
    pf_yourName: 'Participant Name *',
    pf_yourNamePlaceholder: 'Enter your full name',
    pf_emailAddress: 'Email Address *',
    pf_assessmentLanguage: 'Assessment Language',
    pf_emailPlaceholder: 'Enter your email',
    pf_invalidEmail: 'Please enter a valid email address',
    pf_yourAge: 'Your Age *',
    pf_selectAge: 'Select age group',
    pf_yearsUnit: 'years',
    pf_dyslexiaTested: 'Have you been tested for dyslexia?',
    pf_iAmDyslexic: 'Yes, I am officially diagnosed',
    pf_iAmNotDyslexic: 'Tested and not dyslexic',
    pf_notKnown: 'Not Tested',
    pf_parentName: "Parent's Name *",
    pf_parentNamePlaceholder: "Enter parent's name",
    pf_childName: "Child's Name *",
    pf_childNamePlaceholder: "Enter child's name",
    pf_childAgeLabel: "Child's Age *",
    pf_selectChildAge: "Select age group",
    pf_childDyslexiaTested: 'Has your child been tested for dyslexia?',
    pf_yesHasDyslexia: 'Yes, officially diagnosed',
    pf_yesNoDyslexia: 'Tested and not dyslexic',
    pf_noNotTested: 'No, never been tested',
    pf_nativeLangQuestion: 'What language do you speak at home?',
    pf_browserLangDetected: 'Browser language detected:',
    pf_availableInSystem: '✓ Available in our system',
    pf_yesNative: 'Yes',
    pf_othersNative: 'Others',
    pf_specifyNative: 'Please specify your native language *',
    pf_selectNativePlaceholder: 'Select your native language',
    pf_selectNativeError: 'Please select your native language',
    pf_nativeLangNote: 'This helps us provide more accurate assessment results based on your language background. Your browser language ({lang}) was detected automatically - we never use your IP address for language detection.',
    pf_forYourChild: 'For Your Child',
    pf_childExplanation: 'The computer looks at how you read — your face, eyes, and typing — to understand your reading style. Nothing bad happens to your data. Your parent has said it\'s okay, and you can stop whenever you want.',
    pf_parentalConsentLabel: 'Parental Consent *',
    pf_parentalConsentText: "I consent to the processing of my child's (or my own) facial, eye-movement, and keyboard interaction data for dyslexia screening and learning assessment in compliance with GDPR and MDR.",
    pf_dataConsentLabel: 'Data Processing Consent and Privacy Policy *',
    pf_dataConsentText: 'I agree to the processing of my personal data for screening purposes.\nI have read and agree to the Privacy Policy and Terms of Service.',
    pf_consentPre: 'I agree to the',
    pf_consentPrivacyLink: 'Privacy Policy',
    pf_consentBetween: 'and',
    pf_consentTermsLink: 'Terms of Service',
    pf_consentPost: ', including data processing for screening purposes.',
    pf_emailFailedTitle: 'Email Failed to Send',
    pf_checkYourEmail: 'Check Your Email',
    pf_emailSentDesc: 'We sent a 6-digit verification code to {email}. Please check your inbox and enter the code below.',
    pf_emailFailedDesc: 'Failed to send to {email}.',
    pf_tryAgain: 'Try Again',
    pf_sendingEmail: 'Sending...',
    pf_verificationCodeLabel: 'Verification Code *',
    pf_enterCodePlaceholder: 'ENTER 6-DIGIT CODE',
    pf_codeExpiry: 'Enter the 6-digit code from your email. The code expires in 15 minutes.',
    pf_resendCode: "Didn't receive the code? Click to resend",
    pf_sendingNewCode: 'Sending new code...',
    pf_continue: 'Continue',
    pf_continueToConsent: 'Continue to Consent',
    pf_verifyEmailBtn: 'Verify Email',
    pf_verifying: 'Verifying...',
    pf_processing: 'Processing...',
    pf_importantNotice: 'Important Notice',
    pf_disclaimer: 'DysTest is a digital screening tool designed to support early detection and learning insights. It does not provide a medical diagnosis. For an official diagnosis or clinical advice, please consult qualified healthcare professionals.',
            cg_readingAssessment: 'Reading Assessment',
    cg_whatsNext: "Here's what's next",
    cg_twoSections: 'Two short sections — read a story, then answer a few questions',
    cg_readStory: 'Read the story',
    cg_readStoryDesc: 'Take your time — read at your own pace',
    cg_answerQuestions: 'Answer a few questions',
    cg_answerQuestionsDesc: 'Type your answers in your own words — no right or wrong',
    cg_exampleQuestion: 'What happened at the beginning of the story?',
    cg_exampleAnswer: 'The boy walked to the park…',
    cg_startReading: 'Start Reading',
    cg_warmupGame: 'Warm-up Game',
    cg_matchHeading: 'Match the word to the picture',
    cg_matchSubtitle: 'Click a word card, then click the matching picture to complete the pair',
    cg_startMatching: 'Start matching!',
    cg_demoWord: 'cat',
    cg_allMatched: 'All pairs matched!',
    cg_heatmapContinuing: 'Your gaze heatmap is shown below — continuing shortly…',
    cg_lookAtTarget: '👁️ Looking at target!',
    cg_lookAtDot: 'Look at the red dot',
    cg_sRemaining: 's remaining',
    cg_skipFixation: 'Skip Fixation',
    cg_correct: '✓ Correct!',
    cg_tryAgain: '✗ Try Again',
    cg_liveMetrics: 'Live Metrics',
    cg_blinks: 'Blinks',
    cg_matched: 'Matched',
    cg_errors: 'Errors',
    fpc_instruction: 'Look at each dot',
    fpc_dotsTotal: 'dots total — look directly at each dot and hold your gaze. It captures automatically.',
    fpc_cancelCalibration: 'Cancel calibration',
    fpc_clickMe: 'Click me!',
    fpc_eyeTrackingStarting: 'Eye tracking starting — you can already click!',
    fpc_calibrationComplete: 'Calibration Complete!',
    fpc_trainingSamples: '{n} training samples recorded',
    fpc_allPointsDone: 'All points done!',
    fpc_pointOf: 'Point {current} of {total}',
    fpc_training: 'Training',
    fpc_inactive: 'Calibration inactive',
    fpc_savingCalibration: 'Saving calibration…',
    fpc_moveEyes: 'Move your eyes to the dot…',
    fpc_noFace: '⚠️ No face detected — center your face in the camera',
    fpc_missingIrisTitle: '⚠️ Iris data was missing on too many dots',
    fpc_missingIrisBody: 'Several calibration dots were skipped because iris landmarks were not detected.',
    fpc_lowAccuracyTitle: 'Accuracy lower than expected',
    fpc_lowAccuracyBody: 'Calibration error is higher than ideal. This may reduce gaze tracking precision during the assessment.',
    fpc_redoCalibration: 'Redo calibration',
    fpc_continueAnyway: 'Continue anyway',
    fpc_lookAtDot: 'Look directly at the dot',
    fpc_dotReady: '✓ Dot captured — moving on…',
    fpc_keepLooking: 'Keep looking at the dot — it captures automatically',
    fpc_captured: '✓ Captured! Moving to next…',
    fpc_checkingAccuracy: 'Checking accuracy',
    fpc_valGotIt: 'Got it! Moving to next…',
    fpc_valHoldStill: 'Hold still — collecting…',
    fpc_valLookDot: 'Look at the dot — it turns orange when ready',
    fpc_valCheck: 'CHECK',

    acp_welcome: 'Welcome, {name}!',
    acp_setupSubtitle: "Let's set up your assessment",
    acp_setupVerification: 'Setup Verification',
    acp_setupStepsDesc: 'Complete all steps to unlock assessment',
    acp_step1Title: 'Camera Active',
    acp_step1Desc: 'Camera starts automatically — no action needed',
    acp_step2Title: 'Camera Verification',
    acp_step2Desc: 'Verify camera quality and stability',
    acp_step3Title: 'Typing Test',
    acp_step3Desc: 'Verify keyboard functionality',
    acp_step4Title: 'Eye Calibration',
    acp_step4Desc: 'Calibrate eye tracking system',
    acp_eyeTrackingActive: '✓ Eye tracking active',
    acp_eyeTrackingError: '⚠ Eye tracking error:',
    acp_startingEyeTracking: '⏳ Starting eye tracking...',
    acp_cameraReady: '👁 Camera ready',
    acp_waitingForCamera: '📷 Camera starting automatically...',
    acp_stepDone: 'Done',
    acp_stepInProgress: 'In Progress',
    acp_checkingCameraStability: 'Checking camera stability...',
    acp_cameraVerified: 'Camera Verified ✓',
    acp_autoVerifyingCamera: 'Auto-verifying camera...',
    acp_waitingFaceDetection: 'Waiting for face detection...',
    acp_eyeTrackingStarting: 'Starting eye tracking...',
    acp_calibrationInProgress: 'Calibration in progress...',
    acp_eyeTrackingReady: 'Eye Tracking Ready ✓',
    acp_startingCalibration: 'Starting calibration...',
    acp_distanceHint: 'Keep your face centred and still, no more than 60 cm from the screen',
    acp_distanceLabel: 'max 60 cm',
    acp_fpsTitle: 'Your camera is a bit slow',
    acp_fpsBody: 'It delivers {fps} images per second; at least 10 are needed for gaze measurements. These usually help within seconds:',
    acp_fpsVeryLow: 'At this speed some eye-movement measures may not be usable; the other tasks still count.',
    acp_fpsTipLight: 'Turn on more light or face a window',
    acp_fpsTipCharger: 'Plug in the charger (battery saving slows the laptop down)',
    acp_fpsTipApps: 'Close video calls and other heavy programs',
    acp_fpsCheckAgain: 'Check again',
    acp_fpsChecking: 'Checking…',
    acp_fpsContinue: 'Continue anyway',
    acp_preparingEyeTracking: 'Preparing eye tracking...',
    acp_cameraPreview: 'Camera Preview',
    acp_noFaceDetected: 'No face detected',
    acp_live: 'LIVE',
    acp_cameraVerifiedTitle: 'Camera Verified ✓',
    acp_cameraReadyTitle: 'Camera Ready',
    acp_qualityCheckPassed: 'Quality check passed',
    acp_optimizedForAssessment: 'Optimized for assessment',
    acp_verifiedBadge: 'Verified',
    acp_readyBadge: 'Ready',
    acp_assessmentMetrics: 'Assessment Metrics',
    acp_faceDistance: 'Face Distance',
    acp_goodDistance: 'Good distance for assessment',
    acp_searching: 'Searching...',
    acp_lightLevel: 'Light Level',
    acp_fps: 'FPS',
    acp_fpsExcellent: 'Excellent frame rate',
    acp_fpsGood: 'Good frame rate',
    acp_fpsModerate: 'Moderate frame rate',
    acp_fpsLow: 'Low frame rate',
    acp_fpsInitializing: 'Initializing...',
    acp_faceDetection: 'Face Detection',
    acp_faceDetected: '✓ Detected',
    acp_faceDetectedSub: 'Face detected successfully',
    acp_cameraQualityPassed: 'Camera quality check passed. Ready for next step.',
    acp_visionWarnings: 'Vision Warnings',
    acp_trackingMetrics: 'Tracking Metrics',
    acp_headPitch: 'Head Pitch:',
    acp_headYaw: 'Head Yaw:',
    acp_headRoll: 'Head Roll:',
    acp_gazePoint: 'Gaze Point:',
    acp_emotion: 'Emotion:',
    acp_confidence: 'Confidence:',
    acp_trackingId: 'Tracking ID:',
    acp_faceId: 'Face ID:',
    acp_browser: 'Browser:',
    acp_os: 'OS:',
    acp_camera: 'Camera:',
    acp_resolution: 'Resolution:',
    acp_initializingTracking: 'Initializing tracking metrics...',
    acp_dataQuality: 'Data Quality',
    acp_dataQualityExcellent: 'EXCELLENT',
    acp_dataQualityGood: 'GOOD',
    acp_dataQualityFair: 'FAIR',
    acp_dataQualityDetecting: 'DETECTING...',
    acp_dataQualityExcellentDesc: 'Optimal for assessment',
    acp_dataQualityGoodDesc: 'Good for assessment',
    acp_dataQualityFairDesc: 'Acceptable quality',
    acp_dataQualityDetectingDesc: 'Analyzing conditions',
    acp_faceMeshPoints: 'Face Mesh Points',
    acp_completeFaceMesh: 'Complete face mesh',
    acp_detecting: 'Detecting...',
    acp_typingTest: 'Typing Test',
    acp_typeQwerty: 'Type {target} to verify keyboard',
    acp_verifyKeyboard: 'Verify keyboard functionality',
    acp_activeStatus: 'Active',
    acp_lockedStatus: 'Locked',
    acp_typeHere: 'Type here...',
    acp_typingCompleted: '✓ Completed! Proceeding to next step...',
    acp_keepTyping: 'Keep typing:',
    acp_remaining: 'remaining',
    acp_startTyping: 'Start typing the letters above',
    acp_completeCameraVerification: 'Complete camera verification to unlock',
    acp_lightingPerfect: 'Perfect lighting for assessment',
    acp_lightingSlightlyBright: 'Slightly bright - reduce if possible',
    acp_lightingTooBright: 'Too bright - reduce lighting/screen brightness',
    acp_lightingSlightlyDim: 'Slightly dim - increase if possible',
    acp_lightingTooDim: 'Too dim - increase lighting',
    acp_positionFace: '👤 Position your face in camera view',
    acp_faceDetectedStarting: '✅ Face detected - starting verification...',
    acp_adjustDistance: 'Adjust your distance from the camera',
    acp_moveCloser: 'Move closer to the camera',
    acp_moveFarther: 'Move farther from the camera',
    acp_distanceInRange: 'Distance looks good',
    pdg_moveCloser: 'Move closer to the camera',
    pdg_moveFarther: 'Move farther from the camera',
    pdg_dismiss: 'Dismiss',
    acp_noData: 'No data',
    acp_setupProgress: 'Setup Progress',
    acp_stepsComplete: '{n} of 4 steps complete',
    acp_environment: 'Environment',
    acp_checkingEllipsis: 'Checking…',
    acp_waitingEllipsis: 'Waiting…',
    acp_analysingConditions: 'Analysing camera conditions…',
    acp_positionFaceCamera: 'Position your face in the camera',
    acp_faceAligned: 'Face aligned',
    shared_stepOf: 'Step {step} of {total}',
    shared_start: 'start',
    cal_introHeading: 'CALIBRATION',
    cal_introSubtitle: 'Watch the dots and keep your head still',
    story_introHeading: 'READING AND QUESTIONS',
    fpc_lookHere: 'Look here!',
            pfp_chip_gdpr: 'GDPR Compliant',
    pfp_chip_mdr: 'MDR Aligned',
    pfp_chip_noData: 'No Data Sharing',
    pfp_chip_languages: '8 Languages',
    kbv_header: 'Keyboard Dynamics Capture',
    kbv_interKey: 'Inter-key interval',
    kbv_typingSpeed: 'Typing speed',
        pfp_aiBadge: 'Eye-Tracking Dyslexia Screening Tool',
    pfp_whyBadge: 'Why DysTest',
    mat_missionBadge: 'Our Mission & Technology',
    mat_eyeBadge: 'Eye Tracking',
    mat_kbdBadge: 'Keystroke Analysis',
    mat_ferBadge: 'Facial Expression',
    pfp_noBrowserFooter: 'No account needed · Entirely in-browser · Results in <15 minutes',
    eye_viz_landmarks: '478 Landmarks',
    eye_viz_irisFaceMesh: 'Iris + Face mesh',
    eye_viz_fps: '30 FPS real-time',
    eye_viz_validation: '9-Point + 4-Point Validation',
    eye_viz_calibration: 'Clinical-grade 13-point calibration',
    ran_anim_rapidNaming: 'Rapid Naming',
    ran_anim_predictor: '#1 dyslexia predictor',
    ran_anim_ageNormed: 'Age-normed scoring',
    ran_anim_zscore: 'z-score + percentile',
    acp_hideDetails: 'Hide details ▲',
    acp_showDetails: 'Show details ▼',
    acp_headLabel: 'Head',
    acp_setupTipsTitle: 'Quick Setup Tips',
    acp_tip1: 'Sit ~50 cm (arm\'s length) from your screen',
    acp_tip2: 'Ensure even, bright lighting on your face',
    acp_tip3: 'Keep your head level and face the camera directly',
    acp_tip4: 'Use a quiet space for an undistracted assessment',
    acp_progressLabel: 'Progress',

    priv_gdprTitle: 'GDPR Compliant',
    priv_gdpr1: 'Verifiable parental consent for under-13 users',
    priv_gdpr2: 'Data minimization and purpose limitation',
    priv_gdpr3: 'Right to access, rectify, and delete data',
    priv_gdpr4: 'Transparent data processing',
    priv_mdrTitle: 'MDR Aligned',
    priv_mdr1: 'Extra protection for vulnerable subjects',
    priv_mdr2: 'Clinical safety and performance',
    priv_mdr3: 'Clear screening vs diagnosis distinction',
    priv_mdr4: 'Ethical data handling for health data',
    priv_retentionTitle: 'Data Retention Policy',
    priv_12months: '12 Months',
    priv_12monthsDesc: 'Eye-tracking coordinates & keystrokes',
    priv_24months: '24 Months',
    priv_24monthsDesc: 'Analysis Metadata',
    priv_parentsControl: 'Parents Control',
    priv_deleteAnytime: 'Delete anytime',

    mat_headline1: 'HUMAN EMPATHY.',
    mat_headline2: 'ARTIFICIAL PRECISION',
    mat_subheadline: 'Combining lived experience with cutting-edge AI to make dyslexia screening',
    mat_subheadlineHighlight: 'accessible to everyone',
    mat_whatTitle: 'WHAT WE DO',
    mat_what1: 'We combine deep expertise in dyslexic reading patterns with advanced AI technology. After years of research into how dyslexia affects reading and where the brain must work harder, we\'ve taught these insights to an adaptive AI.',
    mat_what2: 'Our AI conducts intelligent, multi-layered tests — each response compared against thousands of real reading patterns. It learns in real time, asking the right follow-up questions and catching subtle cues that traditional tests overlook.',
    mat_what3: 'All to give families the clarity they need to take the next step with confidence.',
    mat_whyTitle: 'WHY WE DO THIS',
    mat_why1: 'Imagine your child facing the same reading challenges you once did — but now, you know it has a name: dyslexia. You want answers, yet the first step is a test costing €750 to €1,800 — unreachable for many families.',
    mat_why2: 'That reality breaks our hearts. Every child deserves to understand how they learn. Every parent deserves answers.',
    mat_why3: 'That\'s why we\'re here — because understanding your own mind should never be a privilege. It\'s a human right.',
    mat_bgTitle: 'OUR BACKGROUND',
    mat_bg1: 'We\'ve lived with dyslexia ourselves and spent over 20 years specializing in accessible reading. By combining this lived experience with AI, we\'ve built a dyslexia screening tool that\'s simple, affordable, and life-changing.',
    mat_bg2: 'Our AI doesn\'t just measure reading speed or count errors — it understands the how behind every eye movement, pause, and recovery. It looks at the cognitive processes that make reading uniquely challenging.',
    mat_techTitle: 'OUR TECHNOLOGY',
    mat_tech1: 'By amplifying these subtle differences, our system learns to distinguish between dyslexia and other reading challenges with extraordinary precision. We use deep clustering — an advanced form of unsupervised learning — to recognize the nuanced features of the dyslexic brain.',
    mat_tech2: 'This multi-layered, cross-referenced approach brings greater certainty than any previous method — going deeper and further than ever before.',
    mat_eyeTitle: 'Eyeball Tracking',
    mat_eyeText: 'We measure gaze stability, fixation duration, and saccadic movement to reveal how visual focus shifts during reading.',
    mat_kbdTitle: 'Keyboard Dynamics',
    mat_kbdText: 'Keystroke rhythm, hesitation, and correction speed expose sequencing and phonological challenges unique to dyslexic profiles.',
    mat_ferTitle: 'Facial Emotion Recognition (FER)',
    mat_ferText: 'Subtle expressions reveal frustration, engagement, or relief — helping our AI sense the emotional layer behind reading effort.',

    footer_about: 'About',
    footer_blog: 'Publications',
    footer_contact: 'Contact',
    footer_faq: 'FAQ',
    footer_privacy: 'Privacy',
    footer_terms: 'Terms of Service',
    footer_tagline: 'Dyslexia screening using real-time iris tracking, keyboard dynamics and facial expression analysis — 15 minutes, no download, no specialist. Available in 8 languages.',
    footer_productHeading: 'Product',
    footer_legalHeading: 'Legal',
    footer_mission: 'Built to make dyslexia screening accessible to everyone, in every language.',
    footer_copyright: 'All rights reserved',
    footer_madeWith: 'Made with purpose for dyslexia awareness',
    footer_allSystems: 'All systems operational',

    fp_assessmentBadge: 'Final step',
    fp_feedbackTitle: 'Share one final note',
    fp_feedbackDesc: 'Optional feedback helps us improve the assessment experience.',
    fp_shareThoughts: 'Share your thoughts',
    fp_optional: 'Optional',
    fp_words: 'Words',
    fp_corrections: 'Corrections',
    fp_speed: 'Speed',
    fp_feedbackPlaceholder: 'Anything difficult, unclear, or worth improving?',
    fp_observationNotesTitle: 'Observation notes (optional)',
    fp_observationNotesDesc: "For the parent or proctor present during the test: anything you noticed that the screen can't see — distraction, fatigue, crying, noise, or anything else worth recording alongside the results.",
    fp_observationNotesPlaceholder: 'e.g. "Seemed tired during the word writing test" or "Got distracted by a sibling during Story 1"',
    fp_generatingReport: 'Generating your report…',
    fp_submitBtn: 'Finish & view report',
    fp_skipHint: 'Feedback is optional. Continue when you are ready.',
    fp_youNailedIt: 'Assessment complete',
    fp_reportGenerated: 'Your report is ready in your dashboard.',
    fp_copySentTo: 'Access details sent to',
    fp_welcomeBack: 'Report saved',
    fp_newReportAdded: 'Added to your dashboard results.',
    fp_reportReady: 'Your report is ready',
    fp_reportSubtitle: 'Full analysis, eye-tracking playback & dyslexia indicators',
    fp_openingReport: 'Opening your report…',
    fp_viewMyReport: 'View My Report',
    fp_signInLater: 'or sign in later at',
    fp_saveForAccess: 'Save for future access',
    fp_emailLabel: 'Email',
    fp_passwordLabel: 'Password',
    fp_copied: 'Copied',
    fp_copy: 'Copy',
    fp_pwUpdated: 'Password updated successfully',
    fp_setCustomPw: 'Set a custom password',
    fp_newPwPlaceholder: 'New password (min 4 chars)',
    fp_save: 'Save',
    fp_cancel: 'Cancel',
    fp_signInLink: 'Open dashboard →',
    fp2_saving: 'Saving your results…',
    fp2_savingSub: 'This takes a few seconds. Please keep this page open.',
    fp2_allDone: 'All done',
    fp2_reportReadySent: 'The report is ready. We sent the login details to',
    fp2_reportReadyReturning: 'The report is ready and has been added to your dashboard.',
    fp2_passwordLine: 'Your password (also in the email):',
    fp2_openReport: 'Open the report',
    fp2_saveFailed: 'Your results couldn\'t be saved yet.',
    fp2_saveFailedSub: 'Check the internet connection and try again. Please keep this page open.',
    fp2_tryAgain: 'Try again',
    fp2_sessionTitle: 'Anything about the test we should know?',
    fp2_sessionOptional: 'Optional · does not change the result',
    fp2_cTired: 'Tired or distracted',
    fp2_cCameraMic: 'Camera or microphone problem',
    fp2_cNoisy: 'Noisy room or interruptions',
    fp2_cLanguage: 'Not the first language',
    fp2_cHelped: 'Someone helped with answers',
    fp2_cFroze: 'Page froze or had to reload',
    fp2_saveNotes: 'Save notes',
    fp2_notesSaved: 'Notes saved',
    fp2_notesFailed: 'The notes could not be saved. Please try again.',
    fp_parentNote: '👨‍👩‍👧 A note for the parent',
    fp_parentNoteDesc: 'The assessment is complete. Please share any observations about how your child engaged with the tasks today (optional).',
    fp_screeningResult: 'Screening Result',
    fp_riskTypical: 'Typical — no significant indicators',
    fp_riskBorderline: 'Borderline — some indicators present',
    fp_riskHighRisk: 'High Risk — several indicators detected',
    fp_riskVeryHighRisk: 'Very High Risk — strong indicators across domains',
    fp_confidence: 'Confidence',
    fp_keyFindings: 'Key Findings',
    fp_disclaimer: 'This is a screening tool only — not a clinical diagnosis. Please consult a registered educational psychologist for a full assessment.',
    fp_notAssessed: 'Not assessed',
    fp_percentile: 'th percentile',
    fp_domainTypical: '✓ Typical',
    fp_domainBorderline: '⚠ Borderline',
    fp_domainHighRisk: '✗ High Risk',
    fp_aiReportTitle: 'AI Personalised Report',
    fp_aiGenerating: 'DysTest AI is generating your personalised report…',
    fp_aiGenerated: 'Generated by DysTest AI · Not a clinical diagnosis',
    fp_aiAnalysing: 'Analysing assessment data…',
    fp_strengths: 'Strengths',
    fp_areasToSupport: 'Areas to Support',
    fp_noDifficulties: 'No significant difficulties detected.',
    fp_eyeTracking: 'Eye tracking:',
    fp_recommendations: 'Recommendations',
    fp_nextStep: 'Next step:',
    pp_title: 'Privacy Policy',
    pp_bannerTitle: 'Your Privacy is Sacred',
    pp_bannerDesc: 'We believe your personal data is yours, not ours to sell. We collect only what\'s necessary to provide you with accurate dyslexia screening, and we protect it with industry-leading security. You have complete control over your information, always.',
    pp_contactTitle: 'Questions About Your Privacy?',
    pp_contactDesc: 'We\'re committed to transparency. If you have any questions about how we handle your data, we\'re here to help.',
    pp_privacyInquiries: 'Privacy Inquiries',
    pp_generalSupport: 'General Support',
    pp_acknowledgment: 'By using the DysTest Service, you acknowledge that you have read and understood this Privacy Policy.',
    tos_title: 'Terms of Service',
    tos_missionTitle: 'Our Mission',
    tos_missionDesc: 'Understanding your own mind shouldn\'t be a luxury — it\'s a human right. Our mission is to make dyslexia screening accessible, affordable, and accurate for every family who needs it.',
    tos_contactTitle: 'Questions About These Terms?',
    tos_contactDesc: 'We\'re here to help. If you have any questions about our Terms of Service, please don\'t hesitate to reach out.',
    tos_acknowledgment: 'By using the DysTest Service, you acknowledge that you have read, understood, and agree to be bound by these Terms of Service.',
    shared_quickNav: 'Quick Navigation',
    shared_backHome: '← Back to Home',
    shared_effectiveDate: 'Effective Date: October 30, 2025',
    shared_lastUpdated: 'Last Updated: October 30, 2025',
    shared_footerTagline: 'Making dyslexia testing accurate, accessible, and affordable.',
    shared_website: 'Website',
    shared_email: 'Email',
    blog_knowledgeBadge: 'DysTest Knowledge Center',
    blog_title: 'DysTest Blog',
    blog_subtitle: 'Expert insights on dyslexia, learning differences, and how AI is revolutionizing early detection and support',
    blog_searchPlaceholder: 'Search articles about dyslexia, learning differences, AI assessment...',
    blog_catAll: 'All',
    blog_catUnderstanding: 'Understanding Dyslexia',
    blog_catEarlyDetection: 'Early Detection',
    blog_catTechnology: 'Technology',
    blog_catNews: 'News',
    blog_featuredBadge: '⭐ Featured',
    blog_featuredArticles: 'Featured Articles',
    blog_allArticles: 'All Articles',
    blog_article: 'article',
    blog_articles: 'articles',
    blog_readMore: 'Read More →',
    blog_found: 'Found',
    blog_forQuery: 'for',
    blog_noResults: 'No articles found',
    blog_noResultsDesc: 'Try adjusting your search or filter criteria',
    blog_clearFilters: 'Clear filters and show all articles',
    blog_newsletterTitle: 'Stay Updated on Dyslexia Research',
    blog_newsletterDesc: 'Get the latest insights on dyslexia research, AI technology, and learning strategies delivered to your inbox.',
    blog_newsletterBtn: 'Subscribe to Updates',
    blog_newsletterPrivacy: 'No spam. Unsubscribe at any time. We respect your privacy.',
    blog_newsletterEmailPlaceholder: 'Enter your email',
    blog_newsletterSending: 'Sending…',
    blog_newsletterCheckInbox: 'Almost done — check your inbox and click the link to confirm.',
    blog_newsletterError: 'Something went wrong. Please try again later.',
    nws_working: 'One moment…',
    nws_confirmedTitle: 'You\'re subscribed',
    nws_confirmedBody: 'Thank you! You\'ll receive new blog posts, technical papers and our newsletter. Every email has an unsubscribe link.',
    nws_wasUnsubscribed: 'This address was unsubscribed. Sign up again on the blog to receive updates.',
    nws_unsubTitle: 'Unsubscribe from DysTest updates?',
    nws_unsubBody: 'You will no longer receive new blog posts, technical papers or the newsletter.',
    nws_unsubBtn: 'Unsubscribe',
    nws_unsubDoneTitle: 'You\'re unsubscribed',
    nws_unsubDoneBody: 'You won\'t receive any more updates. Changed your mind? You can sign up again on the blog.',
    nws_invalidTitle: 'This link is not valid',
    nws_invalidBody: 'The link may be old or incomplete. You can sign up again on the blog.',
    nws_toBlog: 'Go to the blog',
    blog_backToArticles: 'Back to Articles',
    blog_loadingArticle: 'Loading article...',
    blog_articleNotFound: 'Article Not Found',
    blog_ctaTitle: 'Ready to Learn More?',
    blog_ctaDesc: "Discover how DysTest uses eye-gaze tracking and keyboard dynamics to identify learning differences and support every child's potential.",
    blog_tryDystest: 'Try DysTest Assessment',
    blog_readMoreArticles: 'Read More Articles',
    faq_badge: "Got Questions? We've Got Answers",
    faq_title: 'Frequently Asked Questions',
    faq_subtitle: 'Everything you need to know about our',
    faq_subtitleHighlight: 'Eye-Tracking Dyslexia Screening',
    faq_searchPlaceholder: "Search questions... (e.g., 'cost', 'privacy', 'how long')",
    faq_mostPopular: 'Most Popular Questions',
    faq_noResults: 'No questions found matching your search.',
    faq_clearFilters: 'Clear Filters',
    faq_stillHave: 'Still have questions?',
    faq_supportDesc: 'Our support team is here to help you get the answers you need. We typically respond within 24 hours.',
    faq_contactSupport: 'Contact Support',
    faq_startScreening: 'Start Screening',
    faq_catAll: 'All',
    faq_related: 'Related:',
    about_badge: 'Our Story',
    about_title: 'About DysTest',
    about_subtitle: 'We believe every child deserves to be understood — not just tested.',
    about_backHome: '← Back to Home',
    about_ctaTitle: 'Ready to Begin?',
    about_ctaDesc: "Start a compassionate screening for your child today and discover how DysTest helps unlock every learner's potential.",
    about_startScreening: 'Start Screening',
    about_startAssessment: 'Start',
    about_navWhy: 'Why DysTest Exists',
    about_navMissionValues: 'Mission & Values',
    about_navTechnology: 'Technology',
    about_navTeam: 'Mind Behind DysTest',
    ran_audio_preLetter: 'Letter naming. Read each row of letters out loud, as fast as you can.',
    ran_audio_preDigit: 'Now digits. Read each row of numbers out loud, as fast as you can.',
    ran_audio_preColor: 'Now colors. Say the color of each square out loud, as fast as you can.',
    ran_audio_preObject: 'Now objects. Name each picture out loud, as fast as you can.',
    ran_audio_runLetter: 'Read each letter out loud as fast as you can.',
    ran_audio_runDigit: 'Read each number out loud as fast as you can.',
    ran_audio_runColor: 'Say the color of each square out loud as fast as you can.',
    ran_audio_runObject: 'Name each picture out loud as fast as you can.',
    ran_audio_practice: "Let's practise. Say each letter out loud.",
    ran_intro_t1_heading: "Let's say some letters! 🔤",
    ran_intro_t1_desc1: "You'll see big letters on the screen.",
    ran_intro_t1_desc2: 'Say each one out loud as fast as you can!',
    ran_intro_heading: 'RAN',
    ran_intro_desc1: "You'll see rows of letters, digits, colors, and objects. Read each row aloud as quickly as you can — left to right.",
    ran_intro_desc2: "The assessor will press Next row after you finish each row. We'll do a short practice first.",
    ran_intro_labLetters: 'Letters',
    ran_intro_labDigits: 'Digits',
    ran_intro_labColors: 'Colors',
    ran_intro_labObjects: 'Objects',
    ran_practice_t1_heading: 'Can you say these? 👀',
    ran_practice_heading: 'Practice',
    ran_practice_t1_desc: 'Say each letter out loud. Start from the left!',
    ran_practice_desc: "Read this row of letters aloud, left to right. The assessor taps Next row when you're done.",
    ran_practice_hint: 'Say: "a · o · s · p · k" — then tap the button below.',
    ran_btn_try: "Let's try! 🎉",
    ran_btn_practise: "Let's practise",
    ran_btn_skip: 'Skip',
    ran_btn_gotIt: 'Got it — start letters →',
    ran_btn_begin: 'Begin — start reading row 1 →',
    ran_btn_nextRow: 'Next row',
    ran_btn_continue: 'Continue',
    ran_preLetter_title: 'Letter naming  (RAN-L)',
    ran_preLetter_subtitle: '50 letters · 5 rows of 10. Read each row aloud as fast as you can.',
    ran_preLetter_detail: 'The assessor will tap Next row after each row.',
    ran_preLetter_btn: 'Show the grid →',
    ran_preDigit_title: 'Letters done! Now digits  (RAN-D)',
    ran_preDigit_subtitle: 'Now the same task with digits: 2 · 4 · 5 · 7 · 8',
    ran_preDigit_detail: '50 digits · 5 rows of 10 · same rules.',
    ran_preDigit_btn: 'Start digits →',
    ran_preColor_title: 'Digits done! Now colors  (RAN-C)',
    ran_preColor_subtitle: 'Say the color of each square out loud, as fast as you can.',
    ran_preColor_detail: '50 colored squares · 5 rows of 10 · same rules.',
    ran_preColor_btn: 'Start colors →',
    ran_preObject_title: 'Colors done! Now objects  (RAN-O)',
    ran_preObject_subtitle: 'Name each picture out loud, as fast as you can.',
    ran_preObject_detail: '50 objects · 5 rows of 10 · same rules.',
    ran_preObject_btn: 'Start objects →',
    ran_done_t1_heading: 'Amazing work! 🌟',
    ran_done_heading: 'All naming tasks complete!',
    ran_done_t1_desc: 'You said all the letters!',
    ran_done_desc: 'Here are the results:',
    ran_badge: 'Speed Naming Test',
    ran_intro_watchDemo: '👀 Watch how it works',
    ran_intro_t1_lead: 'Look at each letter and say it out loud as fast as you can — going left to right!',
    ran_intro_step1: 'Look at the row',
    ran_intro_step2: 'Say each one aloud',
    ran_intro_step3: 'Go as fast as you can!',
    ran_intro_demoLabel: 'Say every item out loud, as fast as you can!',
    ran_btn_start: 'Start 🚀',
    ran_btn_tapStart: 'Click to Start Row 1',
    ran_btn_listening: 'Listening…',
    ran_btn_repeat: 'Repeat instruction',
    ran_processing: 'Processing…',
    ran_transcribing: 'Transcribing audio with Whisper…',
    ran_transcribingDesc: 'Analysing speech accuracy — this takes a few seconds',
    ran_stat_totalTime: 'Total time',
    ran_stat_itemsPerSec: 'Items/s',
    ran_stat_avgItem: 'Avg item',
    ran_stat_mistakes: 'Mistakes',
    ran_paceHint: 'Highlighted = current row  ·  Assessor controls the pacing',
    ran_running_letter: '🔤 RAN-L — Letters',
    ran_running_digit: '🔢 RAN-D — Numbers',
    ran_running_color: '🎨 RAN-C — Colours',
    ran_running_object: '🦎 RAN-O — Objects',
    ran_header_letter: 'RAN-L',
    ran_header_digit: 'RAN-D',
    ran_header_color: 'RAN-C',
    ran_header_object: 'RAN-O',

    nav_home: 'Home',
    nav_about: 'About',
    nav_resources: 'Resources',
    nav_startAssessment: 'Start Assessment',
    nav_langMenuTitle: 'Assessment language',
    nav_langMenuHint: 'Stories and instructions follow this language',
    nav_techPapers: 'Technical Papers',
    nav_blog: 'Blog',
    nav_guides: 'Guides',
    nav_faq: 'FAQ',

        wwt_badge: 'Word Writing Test',
    wwt_introTitle: 'Remember and type the word',
    wwt_introDesc: 'A word appears for 5 seconds — then it erases itself letter by letter and you type it from memory',
    wwt_demoHint: 'The word disappears — you type it from memory',
    wwt_startWriting: 'Start writing!',
    wwt_rememberWord: 'Remember this word',
    wwt_watchCountdown: 'Watch the countdown — then type it from memory',
    wwt_typeWord: 'Type the word you saw',
    wwt_pressEnter: 'Type it in and press Enter or Next',
    wwt_nextWord: 'Next word',
    wwt_finish: 'Finish',
    wwt_done: 'Well done!',
    wwt_correct: 'words correct',
    wwt_continue: 'Continue',
    wwt_wordOf: 'Word {current} of {total}',
    form_badgeText: 'Step 1/8: Register',
    form_heading: 'Register & Begin',
    form_privacyNote: 'Your data is protected by GDPR and MDR regulations. No personally identifiable information is shared with third parties.',

    // ── Microphone check panel (mic_*) ───────────────────────────────────
    mic_title: 'Microphone Check',
    mic_sub_checking: 'Checking your microphone…',
    mic_sub_ready: 'Microphone ready — tap Test to record',
    mic_sub_recording: 'Recording… speak now',
    mic_sub_retry: "We didn't quite catch that — try again",
    mic_sub_passed: 'Microphone working perfectly!',
    mic_sub_denied: 'Microphone access denied',
    mic_pleaseSpeak: '🗣️ Please speak now…',
    mic_retryBody: "We couldn't hear you clearly. Speak a little louder and try again.",
    mic_testBtn: 'Test Microphone',
    mic_tryAgain: 'Try Again',
    mic_continueBtn: 'Continue',
    mic_skipDenied: 'Skip (no microphone)',
    mic_skip: 'Skip mic check',

    // ── User Dashboard ──────────────────────────────────
    ud_myDashboard: 'My Dashboard',
    ud_myAssessmentReports: 'My Assessment Reports',
    ud_noAssessmentsFound: 'No completed assessments found.',
    ud_assessmentsFoundSingular: '1 completed assessment found.',
    ud_assessmentsFoundPlural: '{n} completed assessments found.',
    ud_loadError: 'Could not load your assessments. Check your connection and refresh the page.',
    ud_serviceUnavailable: 'The server is temporarily unavailable (503). Please wait a moment and try refreshing the page.',
    ud_progressOverTime: 'Progress Over Time',
    ud_longitudinalSubtitle: '{n} assessments · composite z-score trend',
    ud_typical: 'Typical (z ≥ −0.5)',
    ud_borderline: 'Borderline (z < −0.5)',
    ud_highRisk: 'High Risk (z < −1.0)',
    ud_veryHighRisk: 'Very High Risk (z < −1.5)',
    ud_noAssessmentsFor: 'No assessments found for',
    ud_completeAssessment: 'Complete an assessment to see your results here.',
    ud_startAssessment: 'Start an Assessment',
    ud_assessmentReport: 'Assessment Report',
    ud_completed: 'Completed',
    ud_accuracy: 'ACCURACY',
    ud_blinks: 'BLINKS',
    ud_questions: 'QUESTIONS',
    ud_rawData: 'Raw Data (JSON)',
    ud_tabEyeTracking: 'Eye Tracking',
    ud_tabRan: 'RAN Analysis',
    ud_tabStory: 'Story Analysis',
    ud_gazePlaybackTitle: 'Eye Tracking Playback — 3 modes: Grid \xb7 Heatmap \xb7 Path',
    ud_noGazeData: 'No eye tracking data available for this assessment.',
    ud_gazeDataNote: 'Eye tracking data is captured during the live assessment session and stored with the report.',
    ud_ranTitle: 'RAN — Rapid Automatized Naming Results',
    ud_totalTime: 'Total Time',
    ud_rawSpeed: 'Raw Speed',
    ud_correctedSpeed: 'Corrected Speed',
    ud_errors: 'Errors',
    ud_rowTimings: 'Row Timings',
    ud_transcriptComparison: 'Transcript Comparison',
    ud_row: 'Row',
    ud_audioNote: 'Audio playback is available in the full ZIP report \xb7 Download Full Report (ZIP) above',
    ud_storyTitle: 'Story Reading Analysis',
    ud_story1Label: 'Story 1 — Reading Phase',
    ud_story2Label: 'Story 2 — Reading Phase',
    ud_readingTime: 'Reading Time',
    ud_fixations: 'Fixations',
    ud_regressions: 'Regressions',
    ud_avgFixation: 'Avg Fixation',
    ud_saccades: 'Saccades',
    ud_blinkRate: 'Blink Rate',
    ud_gazeStoryTitle: 'Eye Tracking — Story Pages',
    ud_noStoryGaze: 'Story eye tracking data not available for this assessment.',
    ud_footer: 'Your data is stored securely on this device only.',
    ud_changePassword: 'Change Password',
    ud_changePasswordDesc: "Set a new password for your account. You'll use it the next time you sign in.",
    ud_passwordUpdated: 'Password updated successfully',
    ud_newPasswordPlaceholder: 'New password (min. 4 characters)',
    ud_saving: 'Saving…',
    ud_saveNewPassword: 'Save new password',
    ud_passwordTooShort: 'Password must be at least 4 characters.',
    ud_passwordUpdateFailed: 'Could not update your password. Please try again.',
    ud_accuracySuffix: 'accuracy',
    acp_tabCamera: 'Camera',
    acp_tabKeyboard: 'Keyboard',
    acp_tabMicrophone: 'Microphone',
    acp_checkingCameraQuality: 'Checking camera quality — this takes a few seconds',
    acp_allChecksPassed: 'All checks passed!',
    acp_allSystemsReady: 'Camera, keyboard and microphone are ready.',
    mini_step: 'Step',
    scp_setupComplete: '🎉 Setup Complete!',
    scp_allSetSubtitle: "You're all set for the dyslexia assessment",
    scp_setupQualityScore: 'Setup Quality Score',
    scp_excellentSetup: 'Excellent setup quality for accurate assessment',
    scp_completedSteps: '✅ Completed Steps',
    scp_cameraReadiness: 'Camera Readiness',
    scp_cameraStabilityPassed: '10-second stability check passed',
    scp_keyboardCapture: 'Keyboard Capture',
    scp_keystrokeData: 'Clinical keystroke data captured',
    scp_eyeCalibration: 'Eye Calibration',
    scp_calibrationCompleted: 'Calibration completed ({pct}% accuracy)',
    scp_whatsNext: "What's Next?",
    scp_whatsNextBody: "You'll begin the reading assessment where we'll analyze your eye movements, facial expressions, and reading patterns to provide a comprehensive dyslexia screening.",
    scp_startAssessment: 'Start Dyslexia Assessment',
    scp_startingAssessment: 'Starting Assessment...',
    scp_estimatedTime: 'Estimated time: 10-15 minutes',

    // UserDashboard report grid
    ud_view: 'View',
    ud_report_userInfo: 'User Information',
    ud_report_envSetup: 'Environment Setup',
    ud_report_wordIcon: 'Word-Icon Matching',
    ud_report_ran: 'RAN Analysis',
    ud_report_story: 'Story Analysis',
    ud_report_wordWriting: 'Word Writing',
    ud_report_feedback: 'Feedback',
    ud_report_summary: 'Summary',
    ul_platform: 'Dyslexia Assessment Platform',
    ul_invalidCredentials: 'Invalid email or password. Check the credentials shown at the end of your assessment.',
    ul_loginFailed: 'Login failed. Please try again.',
    ul_resetFailed: 'Something went wrong. Please try again.',
    ul_backToSignIn: 'Back to sign in',
    ul_checkEmail: 'Check your email',
    ul_smtpDevNotice: 'SMTP not configured (dev). Temporary password:',
    ul_resetSent: 'If {email} is registered, a new password is on its way.',
    ul_signIn: 'Sign In',
    ul_resetPassword: 'Reset password',
    ul_resetPasswordDesc: "Enter your email and we'll send you a new password.",
    ul_emailAddress: 'Email Address',
    ul_emailPlaceholder: 'your@email.com',
    ul_sending: 'Sending…',
    ul_sendNewPassword: 'Send New Password',
    ul_welcomeBack: 'Welcome back',
    ul_signInSubtitle: 'Sign in to view your assessment results',
    ul_password: 'Password',
    ul_passwordPlaceholder: 'Your password',
    ul_hidePassword: 'Hide password',
    ul_showPassword: 'Show password',
    ul_forgotPassword: 'Forgot your password?',
    ul_signingIn: 'Signing in…',
    ul_signInDashboard: 'Sign In to Your Dashboard',
    ul_firstTime: 'First time here? Your account was created automatically after your assessment. Check your email for login details.',
    ul_footer: 'GDPR & MDR compliant · No video stored',

    mg_title: 'Hang tight for science!',
    mg_body: 'DysTest analyzes your eye movements (pixel-perfect!), your typing speed, and your audio down to the millisecond.\n\nSuper smart tech, but it has one weakness: it doesn\'t work while your screen is wobbling around in your hand. So pop open a laptop or desktop for a reliable result!',
    mg_howTo: 'How to get started',
    mg_step1: 'Open dystest.com on a laptop or desktop',
    mg_step2: 'Make sure your webcam and keyboard are connected',
    mg_step3: 'Use Chrome or Firefox for best results',
    mg_returnHome: 'Return to homepage',
    mg_shareLink: 'Send me the link',
    mg_copyLink: 'Copy link',
    mg_linkCopied: 'Link copied',
    mg_alreadyDone: 'Already done a test?',
    mg_loginLink: 'Log in to see the report',
    mg_emailLabel: 'Your email address',
    mg_emailPlaceholder: 'name@example.com',
    mg_emailSend: 'Send',
    mg_emailSent: 'Sent! Open the email on your laptop or computer to start the test.',
    mg_emailError: 'The email could not be sent. Check the address and try again.',
};

export default en;
